import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import { Box, Grid, Paper, Typography, Button, Divider, CircularProgress, Stack } from "@mui/material";

import { workflowConfigSchema } from "./helper/WorkflowConfigSchema";
import { buildWorkflowSavePayload } from "./helper/WorkflowConfigPayload";

import WorkflowHeaderForm from "./components/WorkflowHeaderForm";
import StepsSidebar from "./components/StepsSideBar";
import StepEditor from "./components/StepEditor";

import { useGetWorkflowConfigrationQuery } from "store/slices/apis/workflowConfigrationSlice";
import { useGetAllRolesQuery } from "store/slices/apis/rolesSlice";
import { useUpdateWorkflowConfigurationMutation } from "store/slices/apis/workflowConfigrationSlice";
import { useDispatch } from "store";
import { closeSnackbar, openSnackbar } from "store/slices/snackbar";

export default function EditWorkflowConfig() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);

  // 1) Load workflow config
  const { data: wfRes, isLoading: isWfLoading, error: wfError } =
    useGetWorkflowConfigrationQuery(id);

  const workflow = wfRes?.data; // <-- your API: {status,message,data:{...}}
  const projectId = workflow?.projectId;

  // console.log(workflow);


  // 2) Load roles AFTER we know projectId
  const {
    data: rolesRes,
    isLoading: isRolesLoading,
    error: rolesError,
  } = useGetAllRolesQuery(
    { projectId },
    { skip: !projectId } // IMPORTANT
  );

  const roles = useMemo(() => {
    const list = rolesRes?.data ?? rolesRes ?? [];
    // normalize to {value,label} if your API returns different
    // Example if API returns [{roleId, roleName}]:
    return list.map((r) => ({
      value: r.id ?? r.value,
      label: r.displayName ?? r.label,
    }));
  }, [rolesRes]);

  // 3) Update mutation
  const [updateWorkflowFun, updateWorkflowRes] = useUpdateWorkflowConfigurationMutation();

  React.useEffect(() => {
    if (updateWorkflowRes?.isSuccess) {
      if (updateWorkflowRes?.data?.status?.toLowerCase() == 'success') {
        openNotification(updateWorkflowRes?.data?.message, 'success')
        navigate(`/projects/${projectId}/settings/workflow`)
      } else {
        openNotification(updateWorkflowRes?.data?.message, 'error')
      }
    }
  }, [updateWorkflowRes?.isSuccess]);


  React.useEffect(() => {
    if (updateWorkflowRes?.isError) {
      // if (updateWorkflowRes?.error?.data?.status?.toLowerCase() == 'error' ) {
      if (updateWorkflowRes?.error?.data?.status) {
        openNotification(updateWorkflowRes?.error?.data?.message ?? 'Something went wrong!', 'error')
      } else {
        openNotification('Something went wrong!', 'error')
      }
      // cancel();
    }
  }, [updateWorkflowRes?.isError]);

  const openNotification = (msg, type = "error", duration = 3000) => {
    dispatch(
      openSnackbar({
        open: true,
        message: msg,
        variant: 'alert',
        alert: {
          color: type
        },
        close: false
      })
    );
    setTimeout(() => dispatch(closeSnackbar()), duration);
  };

  // guards
  const loading = isWfLoading || (isRolesLoading && !!projectId);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (wfError || !workflow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ mb: 2 }}>
          Unable to load workflow configuration.
        </Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 2 }}>
        Edit Workflow Configuration
      </Typography>

      <Formik
        enableReinitialize
        initialValues={workflow}
        validationSchema={workflowConfigSchema}
        onSubmit={async (values, helpers) => {
          try {

            const payload = buildWorkflowSavePayload(values);
            // console.log(payload);

            // await updateWorkflow({ id: values.workflowConfigurationId ?? id, body: payload }).unwrap();
            await updateWorkflowFun(payload).unwrap();
            helpers.resetForm({ values });
            // TODO: snackbar success
          } catch (e) {
            console.error(e);
            // TODO: snackbar error
          }
        }}
      >
        {({ dirty, isValid }) => (
          <Form>
            <Paper sx={{ width: "100%", maxWidth: "100%", p: 2, mb: 2 }}>
              <WorkflowHeaderForm roles={roles} />

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => navigate(-1)} disabled={updateWorkflowRes?.isLoading}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained">
                  Save
                </Button>
              </Stack>
            </Paper>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2 }}>
                  <StepsSidebar
                    roles={roles}
                    selectedStepIndex={selectedStepIndex}
                    onSelect={setSelectedStepIndex}
                  />
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <Paper sx={{ p: 2 }}>
                  <StepEditor
                    roles={roles}
                    selectedStepIndex={selectedStepIndex}
                    onSelectStep={setSelectedStepIndex}
                  />
                </Paper>
              </Grid>

            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
